/**
 * Utility functions to convert between different item types
 */

import type { Item, SoloMeal } from '@/api/use-get-menu-ws';
import type { SoloMealItem } from '@/api/use-get-solomeal-item';
import type { MenuItem } from '@/types/item';

/**
 * Convert SoloMealItem to MenuItem for use in StoreItemSheet
 */
export function soloMealItemToMenuItem(item: SoloMealItem): MenuItem {
  return {
    _id: item._id,
    name: item.name,
    price: item.price,
    image_thumb_url: item.image_thumb_url,
    discounted_price: item.discounted_price,
    is_ai_image: item.is_ai_image ?? false,
    solo_meal_video_url: item.solo_meal_video_url,
    status: item.is_available ? 'ACTIVE' : 'INACTIVE',
  };
}

/**
 * Convert Store Item to MenuItem for use in StoreItemSheet
 */
export function storeItemToMenuItem(item: Item): MenuItem {
  return {
    _id: item._id,
    name: item.name,
    price: item.price,
    description: item.description,
    image_thumb_url: item.image_thumb_url,
    discounted_price: item.discounted_price,
    is_ai_image: item.is_ai_image ?? false,
    status: (item.status === 'ACTIVE' || item.status === 'INACTIVE'
      ? item.status
      : undefined) as 'ACTIVE' | 'INACTIVE' | undefined,
    discount_config: item.discount_config
      ? {
          ...item.discount_config,
          type:
            item.discount_config.type === 'VALUE' ||
            item.discount_config.type === 'PERCENTAGE'
              ? item.discount_config.type
              : 'VALUE',
        }
      : undefined,
  };
}

/**
 * Convert Store SoloMeal to MenuItem for use in StoreItemSheet
 */
export function storeSoloMealToMenuItem(item: SoloMeal): MenuItem {
  return {
    _id: item._id,
    name: item.name,
    price: item.price,
    description: item.description,
    image_thumb_url: item.image_thumb_url,
    discounted_price: item.discounted_price,
    is_ai_image: item.is_ai_image ?? false,
    solo_meal_video_url: item.solo_meal_video_url,
    status: (item.status === 'ACTIVE' || item.status === 'INACTIVE'
      ? item.status
      : undefined) as 'ACTIVE' | 'INACTIVE' | undefined,
    discount_config: item.discount_config
      ? {
          ...item.discount_config,
          type:
            item.discount_config.type === 'VALUE' ||
            item.discount_config.type === 'PERCENTAGE'
              ? item.discount_config.type
              : 'VALUE',
        }
      : undefined,
  };
}

/**
 * Extract tracking metadata from SoloMealItem
 * Returns flat object compatible with GA tracking (string/number values only)
 */
export function extractSoloMealMetadata(item: SoloMealItem) {
  return {
    // Store info
    store_name: item.store_name,
    store_total_sold: item.store_total_sold,

    // Item info
    item_total_sold: item.item_total_sold,
    item_fee: item.discounted_price,
    item_discount: item.price - item.discounted_price,
    item_price: item.price,
    item_discounted_price: item.discounted_price,

    // Location
    distance: item.distance,

    // Rating
    rating: item.rating?.average ?? item.rating?.google_average ?? 0,

    // Flags
    is_available: item.is_available,
    is_temporary_closed: item.is_temporary_closed ?? false,
    trend_item: item.trend_item ?? false,
  };
}

/**
 * Helper function to extract common metadata from store items
 */
function extractCommonStoreMetadata(item: {
  total_sold?: number;
  discounted_price: number;
  price: number;
  trend_item?: boolean;
  is_ai_image?: boolean;
}) {
  return {
    // Item info
    item_total_sold: item.total_sold ?? 0,
    item_fee: item.discounted_price,
    item_discount: item.price - item.discounted_price,

    // Flags
    trend_item: item.trend_item ?? false,
    is_ai_image: item.is_ai_image ?? false,
  };
}

/**
 * Extract tracking metadata from Store Item
 * Returns flat object compatible with GA tracking (string/number values only)
 */
export function extractStoreItemMetadata(item: Item) {
  return extractCommonStoreMetadata(item);
}

/**
 * Extract tracking metadata from Store SoloMeal
 * Returns flat object compatible with GA tracking (string/number values only)
 */
export function extractStoreSoloMealMetadata(item: SoloMeal) {
  return extractCommonStoreMetadata(item);
}
